import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getSession } from '../lib/api.js';

const KEY = 'ma_onboarding_dismissed_v1';

const STEPS: { to: string; title: string; desc: string }[] = [
  { to: '/', title: 'Submit a sample or URL', desc: 'Detonate a file across every sandbox, or scan a URL.' },
  { to: '/search', title: 'Search in plain English', desc: 'Ask the AI assistant to write a threat-intel query.' },
  { to: '/rules', title: 'Fork a detection rule', desc: 'Grab a YARA rule from the library - it becomes your LiveHunt.' },
  { to: '/alerts', title: 'Set up alerts', desc: 'Watch a family, IOC, or hunt and get notified on new matches.' },
  { to: '/graph', title: 'Investigate the graph', desc: 'Pivot across related samples, C2s, and campaigns.' },
];

/**
 * First-run onboarding checklist (Tier 3): a dismissible card that points new signed-in researchers
 * at the highest-value surfaces. Purely local (localStorage), shown once until dismissed.
 */
export function OnboardingChecklist() {
  const signedIn = !!getSession().accessToken;
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!signedIn) return;
    try {
      if (!localStorage.getItem(KEY)) setOpen(true);
    } catch {
      /* storage disabled */
    }
  }, [signedIn]);

  if (!signedIn || !open) return null;
  const dismiss = () => {
    try {
      localStorage.setItem(KEY, '1');
    } catch {
      /* ignore */
    }
    setOpen(false);
  };

  return (
    // Anchored bottom-right, stacked above the agent chat launcher (bottom-4, 56px tall) so the two
    // never collide. Kept clear of the left sidebar entirely, and z-[35] keeps it BELOW the mobile
    // nav drawer (z-50), its backdrop (z-40), modals, and the chat widget (z-[60]) so it can never
    // intercept clicks meant for navigation controls.
    <div className="fixed bottom-24 right-4 z-[35] w-[min(22rem,calc(100vw-2rem))] rounded-xl border border-border-default bg-bg-base shadow-2xl">
      <div className="flex items-center justify-between border-b border-border-subtle px-3 py-2">
        <span className="text-sm font-semibold text-text-primary">Get started with MalwareAnalyzer</span>
        <button onClick={dismiss} aria-label="Dismiss" className="rounded p-1 text-text-muted hover:text-text-primary">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>
      <ol className="max-h-[60vh] space-y-1 overflow-auto p-2">
        {STEPS.map((s, i) => (
          <li key={s.to}>
            <Link
              to={s.to}
              onClick={dismiss}
              className="flex gap-2 rounded-md p-2 transition-colors hover:bg-bg-surface"
            >
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-500/20 text-[11px] font-semibold text-accent-400">
                {i + 1}
              </span>
              <span className="min-w-0">
                <span className="block text-sm text-text-primary">{s.title}</span>
                <span className="block text-xs text-text-muted">{s.desc}</span>
              </span>
            </Link>
          </li>
        ))}
      </ol>
    </div>
  );
}
